import { useFormContext } from "react-hook-form";
import type { ComponentProps, ReactNode } from "react";
import { Button } from "@/components/button";

/**
 * Submit button bound to the parent <Form>. Disables itself while the submit
 * handler is in flight (RHF formState.isSubmitting) to prevent double-submits —
 * the loading/disabled-on-submit behaviour Albert's review #3 asks for — and
 * optionally swaps to a pending label. Must be rendered inside a <Form>.
 *
 * Pass `requireDirty` for "save" forms that should stay disabled until the user
 * actually changes something (RHF formState.isDirty) — the settings-card
 * pattern. Remount the <Form> (e.g. via `key`) after a successful save so the
 * baseline resets and the button disables again.
 */
type FormSubmitProps = Omit<ComponentProps<typeof Button>, "type" | "children"> & {
  children: ReactNode;
  /** Shown while the submit handler is running. Defaults to children. */
  pendingLabel?: ReactNode;
  /** Keep the button disabled until the form is dirty. Default: false. */
  requireDirty?: boolean;
};

export function FormSubmit({
  children,
  pendingLabel,
  disabled,
  requireDirty = false,
  ...rest
}: FormSubmitProps) {
  const {
    formState: { isSubmitting, isDirty },
  } = useFormContext();

  const blocked = disabled || isSubmitting || (requireDirty && !isDirty);

  return (
    <Button type="submit" disabled={blocked} {...rest}>
      {isSubmitting ? (pendingLabel ?? children) : children}
    </Button>
  );
}
